import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  ChevronDown,
  Coins,
  Compass,
  Crosshair,
  Download,
  Flag,
  Gamepad2,
  Layers,
  Lock,
  Map as MapIcon,
  Notebook,
  RotateCcw,
  ScrollText,
  Sparkles,
  Theater,
  Users,
} from "lucide-react";
import { useWorld } from "../state/World";
import { IMAGES } from "../lib/tokens";
import { PLACES as WORLD_PLACES } from "../lib/world";
import AtlasLabels from "../components/AtlasLabels";

import CloudReveal, { hasArrived } from "../components/CloudReveal";
import TopMessage from "../components/TopMessage";
import JudgesLight from "../components/JudgesLight";
import HudDock from "../components/HudDock";
import SiteFooter from "../components/SiteFooter";
import WhyAWorld from "../components/WhyAWorld";
import PlaceTease from "../components/PlaceTease";
import SealedPlaces from "../components/SealedPlaces";

const PLACE_LABEL = {
  "/playforward": "the Arcade doorway",
  "/playforward/cove": "the chair in the Cove",
  "/playforward/play": "your PlayCard",
  "/stageward": "the Graffiti Wall",
  "/stageward/walk": "the steps of Clarity Hall",
  "/stageward/drawing": "on your way to the Drawing Table",
  "/stageward/table": "the Table",
  "/stageward/mission": "your Mission",
};

/** Percentages measured off the founder's island render. The World is the navigation. */
// Names come from lib/world.js — the canonical World -> Place -> Space -> Practice
// taxonomy. Only coordinates and art live here.
const COORDS = {
  playforward: { icon: Gamepad2, accent: "#b0356e", x: 23, y: 17.5, testid: "door-playforward" },
  stageward: { icon: Theater, accent: "#6e1f22", x: 42, y: 77, testid: "door-stageward" },
  "the-arena": { icon: Crosshair, accent: "#8a6a3a", x: 56, y: 19 },
  ops: { icon: Notebook, accent: "#2c6e73", x: 86, y: 23 },
  "mastermind-chamber": { icon: Users, accent: "#3b5c86", x: 71, y: 48 },
  "the-commons": { icon: Users, accent: "#4a7048", x: 14, y: 50 },
  "the-vault": { icon: Archive, accent: "#4a4a52", x: 17, y: 78 },
  "the-bridge": { icon: Compass, accent: "#5b4a86", x: 62, y: 76 },
  "the-horizon-map": { icon: MapIcon, accent: "#5b4a86", x: 84, y: 77 },
};

const PLACES = WORLD_PLACES.filter((p) => COORDS[p.key]).map((p) => ({
  ...p,
  ...COORDS[p.key],
  // The label is the Place — the building. The Practice is named underneath,
  // because a visitor is choosing what to DO, not which door to admire.
  name: p.place,
  sub: p.practice ? `${p.practice} · ${p.sub}` : p.sub,
}));

const LOOP = [
  { icon: Crosshair, label: "Name an Aim", note: "Clarity, out loud" },
  { icon: Coins, label: "Ante on Yourself", note: "Make it cost something" },
  { icon: Compass, label: "Cross the Seam", note: "Same you, same Pouch" },
  { icon: Layers, label: "Empty the Bench", note: "Shards, unsorted" },
  { icon: Sparkles, label: "Pay to Sort", note: "MelzAI clusters it" },
  { icon: Flag, label: "Mission & Mark", note: "Then take it with you" },
];

const FOUNDATIONS = [
  { icon: Coins, label: "Tokens", note: "Your Pouch, one balance everywhere" },
  { icon: Sparkles, label: "MelzAI", note: "One mind, both realms, one memory" },
  {
    icon: ScrollText,
    label: "Your Tokens",
    note: "A proprietary token system for you to use in Endeavorum",
  },
  { icon: Flag, label: "Marks", note: "Milestones that cost nothing" },
  { icon: Notebook, label: "The Journal", note: "One thread through the World" },
  { icon: Download, label: "Export", note: "Markdown or JSON, never held hostage" },
];

/**
 * The Tour is the first prompt. One lit CTA in the sky over Ops, on the
 * plate itself so it never drifts off the artwork.
 */
const StartHere = ({ onStart, beat }) => (
  <motion.div
    className="absolute z-30 -translate-x-1/2 -translate-y-1/2"
    style={{ left: "76%", top: "12%" }}
    data-testid="start-here"
    initial={{ opacity: 0, y: -12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: beat + 1.3 }}
  >
    <motion.button
      type="button"
      data-testid="start-here-button"
      onClick={onStart}
      className="flex flex-col items-center gap-1.5 rounded-full px-8 py-4"
      style={{
        background: "rgba(18,10,6,0.6)",
        border: "1px solid rgba(224,164,74,0.65)",
        backdropFilter: "blur(12px)",
      }}
      animate={{
        boxShadow: [
          "0 0 26px -10px rgba(255,214,140,0.45)",
          "0 0 70px 0px rgba(255,214,140,0.75)",
          "0 0 26px -10px rgba(255,214,140,0.45)",
        ],
        y: [0, -5, 0],
      }}
      transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      whileHover={{ scale: 1.04 }}
    >
      <span className="engrave text-[11px] text-[#ffeec4]">Start here</span>
      <span className="mono text-[8px] tracking-[0.12em] text-white/55">
        MELZAI WILL SHOW YOU THE WORLD
      </span>
    </motion.button>
  </motion.div>
);

export default function Atlas() {
  // The World lands before the words. On a first arrival the clouds part for
  // three seconds and nothing is named until they have gone; on any later visit
  // the same beats play at a quarter of the length.
  const [intro] = useState(() => !hasArrived());
  // ?og=1 renders the plate as a cover crop with the furniture stripped, purely
  // so the 1200x630 share card can be captured from the real page.
  const og = new URLSearchParams(window.location.search).has("og");
  const beat = intro ? 3.5 : 0.5;

  const { me, mark, cross } = useWorld();
  const nav = useNavigate();
  /**
   * Every Place is its own Portal on its own host. Entering one is a real
   * crossing: mint a signed single-use token, hand identity over, land with the
   * same Pouch. Where no separate origin is configured this is transparently an
   * ordinary route change, so the same call works in preview and production.
   */
  const enterPortal = async (portalKey, to) => {
    const { external } = await cross(portalKey, to);
    if (external) return;
    nav(to);
  };
  const enterPlace = (place) => enterPortal(place.key, place.to);
  const [wide, setWide] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (me) mark("atlas", "/");
  }, [me, mark]);

  const resume = (me?.engagements || [])
    .filter((e) => e.realm !== "atlas" && PLACE_LABEL[e.last_state])
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))[0];

  return (
    <div className="bg-[var(--walnut)]">
      {/* ============ the World. Nothing sits on top of it. ============ */}

      <div
        className="relative h-screen w-full overflow-hidden bg-[#0d0804]"
        data-testid="atlas-hero"
      >
        {/* THE PLATE. Contain-fitted and top-anchored, exactly as the founder's
            Design build sets it, so every percentage inside AtlasLabels is a
            true percentage of his artwork at any window size — and it is also
            the query container the engraved type is sized against. */}
        <div className={`world-plate ${og ? "plate-cover" : ""}`}>
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.14, opacity: 0, filter: "blur(14px)" }}
          animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: intro ? 3.4 : 1.4, ease: [0.16, 0.84, 0.24, 1] }}
        >
          <img
            src={IMAGES.ghostWorld}
            alt="The Endeavorum World — the Commons burning warm, seven Places conjuring themselves in the mist"
            className="absolute inset-0 h-full w-full"
          />
          {/* edge vignettes, never a scrim — the art stays bright in the middle */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(13,8,4,0) 58%, rgba(13,8,4,0.2) 88%, rgba(13,8,4,0.5) 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute -left-px bottom-0 top-0 w-[9%]"
            style={{
              background:
                "linear-gradient(90deg, #130c05 0%, rgba(19,12,5,0.92) 18%, rgba(19,12,5,0.62) 44%, rgba(19,12,5,0.24) 72%, rgba(19,12,5,0) 100%)",
            }}
          />
          {/* the same colour-in fade as the sides, across the top: it gives the
              title sequence its contrast and squares nothing off */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[24%]"
            style={{
              background:
                "linear-gradient(180deg, #130c05 0%, rgba(19,12,5,0.92) 14%, rgba(19,12,5,0.7) 38%, rgba(19,12,5,0.34) 66%, rgba(19,12,5,0) 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute -right-px bottom-0 top-0 w-[9%]"
            style={{
              background:
                "linear-gradient(270deg, #130c05 0%, rgba(19,12,5,0.92) 18%, rgba(19,12,5,0.62) 44%, rgba(19,12,5,0.24) 72%, rgba(19,12,5,0) 100%)",
            }}
          />
          <AtlasLabels beat={beat} onEnter={enterPortal} />
          {me && !me.toured && <StartHere onStart={() => nav("/tour")} beat={beat} />}

          {/* The after-statement, delivered on the World itself so it doesn't
              land on the walnut apron below the plate. Positioned in plate
              coordinates (cqh) so a viewport swap doesn't drift it. */}
          <motion.h1
            className="engrave pointer-events-none absolute inset-x-0 z-30 text-center font-extrabold uppercase leading-[1.02] tracking-[0.015em] text-[#fdf3d8]"
            style={{
              bottom: "2.5cqh",
              textShadow:
                "0 2px 10px rgba(0,0,0,1), 0 0 22px rgba(0,0,0,1), 0 0 44px rgba(0,0,0,0.98), 0 0 78px rgba(0,0,0,0.9), 0 0 130px rgba(0,0,0,0.6)",
            }}
            data-testid="offer-headline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: beat + 1.1 }}
          >
            <span className="mx-auto block max-w-[min(92vw,1280px)] text-[clamp(1.25rem,3vw,3.1rem)]">
              {new URLSearchParams(window.location.search).get("claim") === "ambition"
                ? "AMBITION"
                : "ADULTING"}{" "}
              Finally Has A World
            </span>
          </motion.h1>
        </motion.div>
        </div>

        <TopMessage beat={beat} />

        {/* the plate dissolves into the page rather than ending on a hard line */}
        <div
          className="pointer-events-none absolute inset-0 z-[6]"
          style={{
            background:
              "linear-gradient(180deg, rgba(13,8,4,0) 90%, rgba(13,8,4,0.22) 96%, var(--walnut) 100%)",
          }}
        />




        {intro && <CloudReveal />}

        {/* The Tour is the first prompt. It lives on the plate — see StartHere. */}

        <motion.button
          type="button"
          data-testid="atlas-scroll-cue"
          onClick={() =>
            document.getElementById("judges")?.scrollIntoView({ behavior: "smooth" })
          }
          className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/45 bg-black/30 p-2 backdrop-blur-sm md:bottom-3"
          animate={{ y: [0, 6, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.6, repeat: Infinity }}
          aria-label="More about the World"
        >
          <ChevronDown size={16} className="text-white" />
        </motion.button>

        {/* HUD pills anchored just above the bouncing arrow so they land on
            the bottom edge of the World image with a little breathing room.
            Above-the-fold on landing, scrolls away with the page after. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-12 z-30 md:bottom-14">
          <div className="pointer-events-auto">
            <HudDock inline />
          </div>
        </div>
      </div>

      {/* ============ below the fold ============
          Founder's directive (12 Feb 2026): collapse the fold right to the
          Judges section. The old intro block ("You are standing in the
          realm…" + the three-fact panel) was the pitch WE used to give the
          judges before they'd seen anything work; they've now seen it work,
          so the pitch can be earned by the walkthrough itself. */}

      <JudgesLight />

      <div className="mx-auto max-w-[1500px] px-6 py-16 md:px-10">
        {/* Founder's Design revision: the two open Places, each with a
            click-through of its Spaces. Proves the product before anyone has
            committed to anything. */}
        <div className="mb-16">
          <PlaceTease />
        </div>

        {/* Six Places stand lit and shut. Say the count out loud. */}
        <div className="mb-16">
          <SealedPlaces />
        </div>

        {!wide && (
          <div className="mb-12 grid gap-4 sm:grid-cols-2" data-testid="island-grid">
            {PLACES.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.key}
                  type="button"
                  data-testid={p.testid || `coming-soon-${p.key}`}
                  disabled={!p.lit}
                  onClick={p.lit ? () => enterPlace(p) : undefined}
                  className="parchment block overflow-hidden rounded-xl text-left"
                >
                  <div className="flex items-center gap-2 px-3.5 py-2" style={{ background: p.accent }}>
                    <Icon size={11} strokeWidth={2.2} className="text-white" />
                    <span className="tracked text-[8px] text-white">{p.sub}</span>
                  </div>
                  <div className="px-4 py-3">
                    <div className="serif ink text-lg leading-tight">{p.name}</div>
                    <div
                      className="tracked mt-1.5 flex items-center gap-1 text-[8px] font-semibold"
                      style={{ color: p.lit ? p.accent : "rgba(15,37,48,0.45)" }}
                    >
                      {p.lit ? <>Enter →</> : <><Lock size={8} strokeWidth={2.6} /> Coming soon</>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* This block read as "SaaS blah" — two floating boxes of icon-label-note,
            9px labels, 11px body. Rebuilt as an editorial spread: one heading so
            the two halves belong to each other, the loop drawn as a connected
            path rather than a list, and the foundations as a ruled column of
            statements. Type sizes up throughout; the founder's readers are not
            all twenty-five. */}
        <div className="mb-8 max-w-2xl border-t border-white/12 pt-10">
          <p className="tracked ink-mid mb-3 text-[10px] font-semibold">Under the hood</p>
          <h2 className="serif ink text-3xl font-light leading-[1.1] sm:text-4xl">
            Six moves, about three minutes —
            <span className="italic text-[var(--stage-gold)]"> and you leave holding something.</span>
          </h2>
        </div>

        <div className="grid gap-10 lg:grid-cols-[340px_1fr] lg:gap-14">
          <div data-testid="core-loop-rail">
            <div className="tracked ink mb-6 text-[10px] font-semibold">The Core Loop</div>
            <div className="relative space-y-6">
              {/* the path itself — a loop should look like a route, not bullets */}
              <div
                className="absolute bottom-4 left-[17px] top-3 w-px"
                style={{ background: "rgba(255,255,255,0.16)" }}
              />
              {LOOP.map((l, i) => {
                const Icon = l.icon;
                return (
                  <div
                    key={l.label}
                    className="relative flex items-start gap-4"
                    data-testid="core-loop-step"
                  >
                    <div
                      className="relative z-10 mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2"
                      style={{
                        borderColor: "rgba(246,214,150,0.35)",
                        background: "rgba(10,8,6,0.9)",
                      }}
                    >
                      <Icon size={15} strokeWidth={2} className="ink" />
                    </div>
                    <div className="min-w-0 pt-1">
                      <div className="ink text-[16px] font-semibold leading-tight">
                        <span className="text-[var(--stage-gold)]">{i + 1}.</span> {l.label}
                      </div>
                      <div className="ink-mid mt-1 text-[14px] leading-snug">{l.note}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div data-testid="foundations-rail">
            <div className="tracked ink mb-6 text-[10px] font-semibold">
              What holds it all up
            </div>
            <div className="grid gap-x-12 sm:grid-cols-2">
              {FOUNDATIONS.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    className="flex items-start gap-4 border-t border-white/10 py-5 first:border-t-0 sm:first:border-t sm:[&:nth-child(-n+2)]:border-t-0"
                    data-testid="foundation-item"
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.7}
                      className="ink mt-0.5 shrink-0 opacity-70"
                    />
                    <div className="min-w-0">
                      <div className="ink text-[16px] font-semibold leading-tight">{f.label}</div>
                      <div className="ink-mid mt-1 text-[14px] leading-snug">{f.note}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {me?.user?.role === "founder" && (
          <button
            type="button"
            data-testid="founder-console-link"
            onClick={() => nav("/founder")}
            className="parchment ink tracked mt-8 rounded-full px-6 py-3 text-[9px] transition-transform duration-200 hover:-translate-y-0.5"
          >
            Founder console
          </button>
        )}
      </div>

      <WhyAWorld />

      <SiteFooter />
    </div>
  );
}
